// Role-based permission system for AUMS

export const ROLES = {
  ADMIN: 'admin',
  DEALER: 'dealer',
  MEMBER: 'member'
}

// Member subroles
export const MEMBER_SUBROLES = {
  SALES: 'sales',
  BANKER: 'banker',
  SERVICE_MANAGER: 'service_manager',
  FINANCE_MANAGER: 'finance_manager',
  INVENTORY_MANAGER: 'inventory_manager',
  CUSTOMER_SERVICE: 'customer_service',
  MARKETING: 'marketing',
  ACCOUNTANT: 'accountant'
}

export const LOGIN_TYPES = {
  ADMIN: 'admin',
  DEALER: 'dealer',
  MEMBER: 'member'
}

export const PERMISSIONS = {
  // Lead permissions
  VIEW_ALL_LEADS: 'view_all_leads',
  CREATE_LEAD: 'create_lead',
  EDIT_LEAD: 'edit_lead',
  DELETE_LEAD: 'delete_lead',
  ASSIGN_LEAD: 'assign_lead',
  
  // Booking permissions
  VIEW_ALL_BOOKINGS: 'view_all_bookings',
  CREATE_BOOKING: 'create_booking',
  EDIT_BOOKING: 'edit_booking',
  DELETE_BOOKING: 'delete_booking',
  APPROVE_BOOKING: 'approve_booking',
  
  // Team permissions
  VIEW_TEAM: 'view_team',
  MANAGE_TEAM: 'manage_team',
  ADD_TEAM_MEMBER: 'add_team_member',
  REMOVE_TEAM_MEMBER: 'remove_team_member',
  
  // Vehicle permissions
  VIEW_VEHICLES: 'view_vehicles',
  MANAGE_VEHICLES: 'manage_vehicles',
  ADD_VEHICLE: 'add_vehicle',
  EDIT_VEHICLE: 'edit_vehicle',
  DELETE_VEHICLE: 'delete_vehicle',
  
  // Dealer permissions
  VIEW_DEALERS: 'view_dealers',
  MANAGE_DEALERS: 'manage_dealers',
  ADD_DEALER: 'add_dealer',
  EDIT_DEALER: 'edit_dealer',
  DELETE_DEALER: 'delete_dealer',
  
  // Pricing permissions
  VIEW_PRICING: 'view_pricing',
  MANAGE_PRICING: 'manage_pricing',
  
  // Service Area permissions
  VIEW_SERVICE_AREAS: 'view_service_areas',
  MANAGE_SERVICE_AREAS: 'manage_service_areas',
  CREATE_SERVICE_AREA: 'create_service_area',
  EDIT_SERVICE_AREA: 'edit_service_area',
  DELETE_SERVICE_AREA: 'delete_service_area',

  // Analytics permissions
  VIEW_ANALYTICS: 'view_analytics',
  VIEW_FINANCIAL_REPORTS: 'view_financial_reports',
  
  // System permissions
  MANAGE_SETTINGS: 'manage_settings',
  MANAGE_INTEGRATIONS: 'manage_integrations',
  VIEW_AUDIT_LOGS: 'view_audit_logs'
}

// Role-Permission mapping
export const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: [
    // Admin has all permissions except team management (which is dealer-level only)
    PERMISSIONS.VIEW_ALL_LEADS,
    PERMISSIONS.CREATE_LEAD,
    PERMISSIONS.EDIT_LEAD,
    PERMISSIONS.DELETE_LEAD,
    PERMISSIONS.ASSIGN_LEAD,
    PERMISSIONS.VIEW_ALL_BOOKINGS,
    PERMISSIONS.CREATE_BOOKING,
    PERMISSIONS.EDIT_BOOKING,
    PERMISSIONS.DELETE_BOOKING,
    PERMISSIONS.APPROVE_BOOKING,
    // Team permissions removed from admin - only dealers manage teams
    PERMISSIONS.VIEW_VEHICLES,
    PERMISSIONS.MANAGE_VEHICLES,
    PERMISSIONS.ADD_VEHICLE,
    PERMISSIONS.EDIT_VEHICLE,
    PERMISSIONS.DELETE_VEHICLE,
    PERMISSIONS.VIEW_DEALERS,
    PERMISSIONS.MANAGE_DEALERS,
    PERMISSIONS.ADD_DEALER,
    PERMISSIONS.EDIT_DEALER,
    PERMISSIONS.DELETE_DEALER,
    PERMISSIONS.VIEW_PRICING,
    PERMISSIONS.MANAGE_PRICING,
    PERMISSIONS.VIEW_SERVICE_AREAS,
    PERMISSIONS.MANAGE_SERVICE_AREAS,
    PERMISSIONS.CREATE_SERVICE_AREA,
    PERMISSIONS.EDIT_SERVICE_AREA,
    PERMISSIONS.DELETE_SERVICE_AREA,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_FINANCIAL_REPORTS,
    PERMISSIONS.MANAGE_SETTINGS,
    PERMISSIONS.MANAGE_INTEGRATIONS,
    PERMISSIONS.VIEW_AUDIT_LOGS
  ],
  
  [ROLES.DEALER]: [
    // Dealer has permissions within their dealership (except vehicle management)
    PERMISSIONS.VIEW_ALL_LEADS,
    PERMISSIONS.CREATE_LEAD,
    PERMISSIONS.EDIT_LEAD,
    PERMISSIONS.DELETE_LEAD,
    PERMISSIONS.ASSIGN_LEAD,
    PERMISSIONS.VIEW_ALL_BOOKINGS,
    PERMISSIONS.CREATE_BOOKING,
    PERMISSIONS.EDIT_BOOKING,
    PERMISSIONS.DELETE_BOOKING,
    PERMISSIONS.APPROVE_BOOKING,
    PERMISSIONS.VIEW_TEAM,
    PERMISSIONS.MANAGE_TEAM,
    PERMISSIONS.ADD_TEAM_MEMBER,
    PERMISSIONS.REMOVE_TEAM_MEMBER,
    PERMISSIONS.VIEW_VEHICLES, // Can view vehicles but not manage them
    PERMISSIONS.VIEW_PRICING,
    PERMISSIONS.MANAGE_PRICING, // Dealers manage their own pricing
    PERMISSIONS.VIEW_SERVICE_AREAS,
    PERMISSIONS.MANAGE_SERVICE_AREAS,
    PERMISSIONS.CREATE_SERVICE_AREA,
    PERMISSIONS.EDIT_SERVICE_AREA,
    PERMISSIONS.DELETE_SERVICE_AREA,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_FINANCIAL_REPORTS,
    PERMISSIONS.MANAGE_SETTINGS
  ],
  
  [ROLES.MEMBER]: [
    // Base member permissions (will be extended based on subrole)
    PERMISSIONS.VIEW_VEHICLES,
    PERMISSIONS.VIEW_ANALYTICS
  ]
}

// Member subrole-specific permissions
export const MEMBER_SUBROLE_PERMISSIONS = {
  [MEMBER_SUBROLES.SALES]: [
    PERMISSIONS.CREATE_LEAD,
    PERMISSIONS.EDIT_LEAD,
    PERMISSIONS.VIEW_ALL_LEADS,
    PERMISSIONS.CREATE_BOOKING,
    PERMISSIONS.EDIT_BOOKING,
    PERMISSIONS.VIEW_ALL_BOOKINGS,
    PERMISSIONS.VIEW_TEAM,
    PERMISSIONS.VIEW_VEHICLES,
    PERMISSIONS.VIEW_PRICING,
    PERMISSIONS.VIEW_ANALYTICS
  ],
  
  [MEMBER_SUBROLES.BANKER]: [
    PERMISSIONS.VIEW_ALL_LEADS,
    PERMISSIONS.VIEW_ALL_BOOKINGS,
    PERMISSIONS.APPROVE_BOOKING,
    PERMISSIONS.VIEW_TEAM,
    PERMISSIONS.VIEW_VEHICLES,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_FINANCIAL_REPORTS
  ],
  
  [MEMBER_SUBROLES.SERVICE_MANAGER]: [
    PERMISSIONS.VIEW_ALL_LEADS,
    PERMISSIONS.VIEW_ALL_BOOKINGS,
    PERMISSIONS.VIEW_TEAM,
    PERMISSIONS.VIEW_VEHICLES,
    PERMISSIONS.VIEW_ANALYTICS
  ],
  
  [MEMBER_SUBROLES.FINANCE_MANAGER]: [
    PERMISSIONS.VIEW_ALL_LEADS,
    PERMISSIONS.VIEW_ALL_BOOKINGS,
    PERMISSIONS.VIEW_TEAM,
    PERMISSIONS.VIEW_VEHICLES,
    PERMISSIONS.VIEW_PRICING,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_FINANCIAL_REPORTS
  ],
  
  [MEMBER_SUBROLES.INVENTORY_MANAGER]: [
    PERMISSIONS.VIEW_VEHICLES, // Can only view vehicles, not manage them
    PERMISSIONS.VIEW_ANALYTICS
  ],
  
  [MEMBER_SUBROLES.CUSTOMER_SERVICE]: [
    PERMISSIONS.CREATE_LEAD,
    PERMISSIONS.EDIT_LEAD,
    PERMISSIONS.VIEW_ALL_LEADS,
    PERMISSIONS.VIEW_ALL_BOOKINGS,
    PERMISSIONS.VIEW_VEHICLES,
    PERMISSIONS.VIEW_ANALYTICS
  ],
  
  [MEMBER_SUBROLES.MARKETING]: [
    PERMISSIONS.VIEW_ALL_LEADS,
    PERMISSIONS.CREATE_LEAD,
    PERMISSIONS.VIEW_ALL_BOOKINGS,
    PERMISSIONS.VIEW_TEAM,
    PERMISSIONS.VIEW_VEHICLES,
    PERMISSIONS.VIEW_ANALYTICS
  ],
  
  [MEMBER_SUBROLES.ACCOUNTANT]: [
    PERMISSIONS.VIEW_ALL_BOOKINGS,
    PERMISSIONS.VIEW_TEAM,
    PERMISSIONS.VIEW_VEHICLES,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_FINANCIAL_REPORTS
  ]
}

/**
 * Check if a user has a specific permission
 * @param {Object} userProfile - User profile object
 * @param {string} permission - Permission to check
 * @returns {boolean} - Whether user has permission
 */
export const hasPermission = (userProfile, permission) => {
  if (!userProfile || !userProfile.role) {
    return false
  }
  
  const userRole = userProfile.role
  const rolePermissions = ROLE_PERMISSIONS[userRole] || []
  
  // For members, check subrole permissions as well
  if (userRole === ROLES.MEMBER && userProfile.subrole) {
    const subrolePermissions = MEMBER_SUBROLE_PERMISSIONS[userProfile.subrole] || []
    return [...rolePermissions, ...subrolePermissions].includes(permission)
  }
  
  return rolePermissions.includes(permission)
}

/**
 * Check if a user has any of the specified permissions
 * @param {Object} userProfile - User profile object
 * @param {Array} permissions - Array of permissions to check
 * @returns {boolean} - Whether user has any of the permissions
 */
export const hasAnyPermission = (userProfile, permissions) => {
  return permissions.some(permission => hasPermission(userProfile, permission))
}

/**
 * Check if a user has all of the specified permissions
 * @param {Object} userProfile - User profile object
 * @param {Array} permissions - Array of permissions to check
 * @returns {boolean} - Whether user has all permissions
 */
export const hasAllPermissions = (userProfile, permissions) => {
  return permissions.every(permission => hasPermission(userProfile, permission))
}

/**
 * Get all permissions for a user role
 * @param {string} role - User role
 * @returns {Array} - Array of permissions
 */
export const getRolePermissions = (role) => {
  return ROLE_PERMISSIONS[role] || []
}

/**
 * Check if user can access a specific pincode
 * @param {Object} userProfile - User profile object
 * @param {string} pincode - Pincode to check
 * @returns {boolean} - Whether user can access the pincode
 */
export const canAccessPincode = (userProfile, pincode) => {
  if (!userProfile || !userProfile.allowedPincodes) {
    return false
  }
  
  // Admin can access all pincodes
  if (userProfile.role === ROLES.ADMIN) {
    return true
  }
  
  return userProfile.allowedPincodes.includes(pincode)
}

/**
 * Filter data based on user's pincode access
 * @param {Array} data - Array of data objects
 * @param {Object} userProfile - User profile object
 * @param {string} pincodeField - Field name containing pincode (default: 'pincode')
 * @returns {Array} - Filtered data array
 */
export const filterByPincodeAccess = (data, userProfile, pincodeField = 'pincode') => {
  if (!userProfile || !Array.isArray(data)) {
    return []
  }
  
  // Admin can see all data
  if (userProfile.role === ROLES.ADMIN) {
    return data
  }
  
  return data.filter(item => canAccessPincode(userProfile, item[pincodeField]))
}

/**
 * Check if user is admin
 * @param {Object} userProfile - User profile object
 * @returns {boolean} - Whether user is admin
 */
export const isAdmin = (userProfile) => {
  return userProfile?.role === ROLES.ADMIN
}

/**
 * Check if user is dealer
 * @param {Object} userProfile - User profile object
 * @returns {boolean} - Whether user is dealer
 */
export const isDealer = (userProfile) => {
  return userProfile?.role === ROLES.DEALER
}

/**
 * Check if user is member
 * @param {Object} userProfile - User profile object
 * @returns {boolean} - Whether user is member
 */
export const isMember = (userProfile) => {
  return userProfile?.role === ROLES.MEMBER
}

/**
 * Check if user is dealer or above (dealer, admin)
 * @param {Object} userProfile - User profile object
 * @returns {boolean} - Whether user is dealer or above
 */
export const isDealerOrAbove = (userProfile) => {
  return [ROLES.ADMIN, ROLES.DEALER].includes(userProfile?.role)
}

/**
 * Check if user is member with specific subrole
 * @param {Object} userProfile - User profile object
 * @param {string} subrole - Subrole to check
 * @returns {boolean} - Whether user is member with specific subrole
 */
export const isMemberWithSubrole = (userProfile, subrole) => {
  return userProfile?.role === ROLES.MEMBER && userProfile?.subrole === subrole
}

/**
 * Get member subrole display name
 * @param {string} subrole - Subrole key
 * @returns {string} - Display name for subrole
 */
export const getMemberSubroleDisplayName = (subrole) => {
  const displayNames = {
    [MEMBER_SUBROLES.SALES]: 'Sales Executive',
    [MEMBER_SUBROLES.BANKER]: 'Banking Executive',
    [MEMBER_SUBROLES.SERVICE_MANAGER]: 'Service Manager',
    [MEMBER_SUBROLES.FINANCE_MANAGER]: 'Finance Manager',
    [MEMBER_SUBROLES.INVENTORY_MANAGER]: 'Inventory Manager',
    [MEMBER_SUBROLES.CUSTOMER_SERVICE]: 'Customer Service',
    [MEMBER_SUBROLES.MARKETING]: 'Marketing Executive',
    [MEMBER_SUBROLES.ACCOUNTANT]: 'Accountant'
  }
  return displayNames[subrole] || subrole
}

/**
 * Check if user can access dealer management features
 * @param {Object} userProfile - User profile object
 * @returns {boolean} - Whether user can manage dealers
 */
export const canManageDealers = (userProfile) => {
  return isAdmin(userProfile)
}

/**
 * Check if user can access vehicle management features
 * @param {Object} userProfile - User profile object
 * @returns {boolean} - Whether user can manage vehicles
 */
export const canManageVehicles = (userProfile) => {
  return isAdmin(userProfile) // Only admin can manage vehicles
}

/**
 * Check if user can access team management features
 * @param {Object} userProfile - User profile object
 * @returns {boolean} - Whether user can manage team
 */
export const canManageTeam = (userProfile) => {
  return isDealer(userProfile) // Only dealers can manage teams, not admin
}

/**
 * Check if user can access pricing management features
 * @param {Object} userProfile - User profile object
 * @returns {boolean} - Whether user can manage pricing
 */
export const canManagePricing = (userProfile) => {
  return isDealerOrAbove(userProfile) // Dealers manage their own pricing, admin can manage all
}

/**
 * Check if user can access service area management features
 * @param {Object} userProfile - User profile object
 * @returns {boolean} - Whether user can manage service areas
 */
export const canManageServiceAreas = (userProfile) => {
  return hasPermission(userProfile, PERMISSIONS.MANAGE_SERVICE_AREAS)
}


/**
 * Get user's login type
 * @param {Object} userProfile - User profile object
 * @returns {string} - Login type (admin, dealer, or member)
 */
export const getUserLoginType = (userProfile) => {
  return userProfile?.role || LOGIN_TYPES.MEMBER
}

/**
 * Get all permissions for a user (including subrole permissions)
 * @param {Object} userProfile - User profile object
 * @returns {Array} - Array of all permissions
 */
export const getAllUserPermissions = (userProfile) => {
  if (!userProfile || !userProfile.role) {
    return []
  }
  
  const rolePermissions = ROLE_PERMISSIONS[userProfile.role] || []
  
  // For members, include subrole permissions
  if (userProfile.role === ROLES.MEMBER && userProfile.subrole) {
    const subrolePermissions = MEMBER_SUBROLE_PERMISSIONS[userProfile.subrole] || []
    return [...rolePermissions, ...subrolePermissions]
  }
  
  return rolePermissions
}
